function isLocalStorageAvailable() {
  try {
    const key = "__test__";
    localStorage.setItem(key, "1");
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

class FormAutosave extends HTMLElement {
  connectedCallback() {
    if (!isLocalStorageAvailable()) return;
    this.storageKey = this.getAttribute("storage-key") || "form-autosave";
    this.form = this.querySelector("form");

    // No form present — the element is used purely to clear persisted state,
    // e.g. on a success/confirmation page reached after a successful submit.
    if (!this.form) {
      this.clear();
      return;
    }

    this.intervalMs = Number(this.getAttribute("interval") || 5000);

    // When the server has re-rendered the form with submitted values (e.g. a
    // validation error), those values are authoritative. Autosaving continues
    // so the draft stays in sync for any later accidental reload.
    if (!this.hasAttribute("server-prefilled")) {
      this.restore();
    }

    this.form.addEventListener("blur", () => this.save(), true);
    this._interval = setInterval(() => this.save(), this.intervalMs);
    this.form.addEventListener("reset", () => this.clear());
  }

  disconnectedCallback() {
    clearInterval(this._interval);
  }

  save() {
    const data = {};
    const formData = new FormData(this.form);
    for (const [key, value] of formData.entries()) {
      // Hidden fields are server-populated, not user input — don't persist them.
      if (this.form.elements[key]?.type === "hidden") continue;
      data[key] = value;
    }
    // Also save radio
    for (const input of this.form.querySelectorAll("input[type=radio]")) {
      if (input.checked) data[`__radio_${input.name}`] = input.value;
    }
    localStorage.setItem(this.storageKey, JSON.stringify(data));
  }

  restore() {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) return;

    try {
      const data = JSON.parse(raw);
      for (const [key, value] of Object.entries(data)) {
        if (key.startsWith("__radio_")) {
          const name = key.replace("__radio_", "");
          const radio = this.form.querySelector(
            `input[type=radio][name="${name}"][value="${value}"]`,
          );
          if (radio) {
            radio.checked = true;
            radio.dispatchEvent(new Event("change", { bubbles: true }));
          }
          continue;
        }
        const field = this.form.elements[key];
        if (
          field &&
          field.type !== "radio" &&
          field.type !== "submit" &&
          field.type !== "hidden"
        ) {
          field.value = value;
          if (field.disabled && value) field.disabled = false;
        }
      }
    } catch (e) {
      console.warn("Failed to restore form state", e);
    }
  }

  clear() {
    localStorage.removeItem(this.storageKey);
  }
}

customElements.define("form-autosave", FormAutosave);
